import React, { useState, useEffect, useCallback } from 'react';
import { Navigate } from "react-router-dom";

import { Button } from "@mui/material";

import Grid from '@mui/material/Grid2';

import { ErrorLog, ModelBase, User } from "@models";

import API from "@dndUtilities/system_api";

import LoadingBox from "@components/input/LoadingBox";
import StringBox from "@components/input/strings/StringBox";
import DescriptionBox from "@dndComponents/DescriptionBox";
import ArrowBackButton from "@components/input/buttons/icon_buttons/ArrowBackButton";

const api = API.getInstance();
// const common_util = DataUtilities.getInstance();

interface Props {
  obj?: ModelBase;
}

export default function ErrorLogEdit({ obj: param_obj }: Props) {
  const [loading, setLoading] = useState(false);
  const [fetched, setFetched] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [reloading, setReloading] = useState(false);
  const [navigateTo, setNavigateTo] = useState<string | null>(null);
  const [obj, setObj] = useState<ErrorLog>(param_obj instanceof ErrorLog ? param_obj : api.applyWorkingOn(new ErrorLog()));
  const [users, setUsers] = useState<User[]>([]);
  const [ping, setPing] = useState(false);
  
  const disabled = api.user === null || !api.online || (!api.user.admin && obj._id !== "" && obj.owner_id !== api.user._id);
  
  const load = useCallback( async () => {
    const user_ids = [obj.user_id];
    const res3 = await api.getObjects({ data_type: "user", filter: { "_id*ObjectIDs": user_ids } });
    setUsers(res3 as User[]);
    setLoading(false);
  }, [obj]);

  useEffect(() => {
    if(!fetched) {
      setFetched(true);
      setLoading(true);
      setTimeout(() => {
        load();
      }, 100);
    }
  }, [load, fetched]);
    
  useEffect(() => {
    if (reloading) {
      setReloading(false);
    }
  }, [reloading, setReloading]);

  const submit = () => {
    if (api.user && 
      (api.user.admin || 
        obj._id === "" || 
        obj.owner_id === api.user._id)) {
      setProcessing(true);
      if (!api.user.admin || obj.source_books_old.filter(o => o.id === "Personal").length > 0) {
        obj.owner_id = api.user._id;
      } else if (api.user.admin && obj.source_books_old.filter(o => o.type === "Homebrew").length === 0) {
        obj.owner_id = "";
      }
      api.upsertObject("error_log", obj).then(res => {
        console.log(res);
        if (res && res.body.id) {
          obj._id = res.body.id;
        } 
        setProcessing(false);
        setNavigateTo("/error_log");
      });
    }
  }
  
  const height = window.innerHeight - 64;
  if (!fetched || loading) {
    return (<LoadingBox size={height * 0.5} height={height} width={window.innerWidth} />);
  } else if (navigateTo !== null) {
    return <Navigate to={navigateTo} />;
  } else {
    return (
      <Grid container spacing={0} direction="row">
        <Grid
          className="header"
          size={{
            xs: 12
          }}>
          <ArrowBackButton 
            onClick={() => { setNavigateTo("/error_log"); }}
          />
          &nbsp;
          Error Log
        </Grid>
        <Grid
          size={{
            xs: 12
          }}>
          <StringBox 
            name="Subject"
            value={obj.name} 
            message={obj.name.length > 0 ? "" : "Subject Invalid"}
            disabled={disabled}
            onBlur={(value: string) => {
              const new_obj = obj;
              new_obj.name = value;
              setObj(new_obj);
              setPing(!ping);
            }}
          />
        </Grid>
        <Grid
          style={{ height: "calc(100vh - 205px)", overflowY: "auto" }}
          size={12}>
          <Grid container spacing={0} direction="row">
            <Grid size={12}>
              <DescriptionBox
                name="Text"
                value={obj.description}
                disabled={disabled}
                onChange={(changed: string) => {
                  const new_obj = obj;
                  new_obj.description = changed;
                  setObj(new_obj);
                  setPing(!ping);
                }}
              />
            </Grid>
            <Grid size={12}>
              <strong>Exception:</strong> { obj.exception }
            </Grid>
            <Grid size={12}>
              <strong>Message:</strong> { obj.message }
            </Grid>
            <Grid size={12}>
              <strong>User:</strong> { users.map(o => o.use_name) }
            </Grid>
            <Grid size={12}>
              <strong>File:</strong> { obj.file }
            </Grid>
            <Grid size={12}>
              <strong>Line:</strong> { obj.line_id }
            </Grid>
          </Grid>
        </Grid>
        { !disabled &&
          <Grid
            size={{
              xs: 12
            }}>
            <Button
              variant="contained"
              color="primary"
              disabled={processing}
              onClick={submit}>
              Submit
            </Button>
            <Button
              variant="contained"
              disabled={processing}
              style={{ marginLeft: "4px" }}
              onClick={ () => { 
                setNavigateTo(`/error_log`);
              }}>
              Cancel
            </Button>
          </Grid>
        }
      </Grid>
    );
  }
}